import type { GenerationCreator } from '../generation-creator'
import { GEMINI_VIDEO_MODEL_MANIFEST } from './gemini-video-model-manifest'
import { RUNWAY_VIDEO_MODEL_MANIFEST } from './runway-video-model-manifest'
import type {
  GeminiVideoPricing,
  RunwayCreditPricing,
  VideoAspectRatio,
  VideoMode,
  VideoModelCapabilityDefinition,
  VideoModelDefinition,
  VideoProvider,
  VideoReferenceLimits,
  VideoResolution
} from './video-model-types'

export { VIDEO_ASPECT_RATIOS, VIDEO_MODES, VIDEO_RESOLUTIONS } from './video-model-types'
export type { VideoAspectRatio, VideoMode, VideoProvider, VideoResolution } from './video-model-types'

export const VIDEO_PRICING_VERSION = 'mixed-media-2026-11-04'
export const RUNWAY_CREDIT_PRICE_USD = 0.01

/** Aggregated compatibility registry. Edit the provider manifests instead. */
export const VIDEO_MODEL_REGISTRY = {
  ...RUNWAY_VIDEO_MODEL_MANIFEST,
  ...GEMINI_VIDEO_MODEL_MANIFEST
} satisfies Readonly<Record<string, VideoModelDefinition>>

export type VideoModel = keyof typeof VIDEO_MODEL_REGISTRY
// Non-empty tuple so consumers can pass it straight to z.enum().
export const VIDEO_MODELS = Object.keys(VIDEO_MODEL_REGISTRY) as [VideoModel, ...VideoModel[]]

export type VideoModelCapability = VideoModelCapabilityDefinition & {
  id: VideoModel
  provider: VideoProvider
}

export type VideoModelMetadata = {
  modelKey: VideoModel
  modelName: string
  isPreview: boolean
}

/** Builds one typed record per registered video model. */
function mapVideoModels<T>(select: (model: VideoModel, definition: VideoModelDefinition) => T): Record<VideoModel, T> {
  const result: Partial<Record<VideoModel, T>> = {}
  for (const model of VIDEO_MODELS) result[model] = select(model, VIDEO_MODEL_REGISTRY[model])
  // Complete: the loop visits every key in VIDEO_MODELS, which is derived from the registry.
  return result as Record<VideoModel, T>
}

export const VIDEO_MODEL_CAPABILITIES = mapVideoModels<VideoModelCapability>((model, definition) => ({
  id: model,
  provider: definition.provider,
  ...definition.capabilities
}))

export const VIDEO_MODEL_METADATA = mapVideoModels<VideoModelMetadata>((model, definition) => ({
  modelKey: model,
  modelName: definition.modelName,
  isPreview: definition.isPreview
}))

export const VIDEO_PROVIDER_RATIOS = mapVideoModels((_model, definition) => definition.providerRatios)

const GEMINI_OMNI_PRICING = GEMINI_VIDEO_MODEL_MANIFEST.gemini_omni_flash.pricing
/** @deprecated Read the Gemini video manifest directly. */
export const GEMINI_OMNI_720P_ESTIMATED_COST_PER_SECOND_USD =
  GEMINI_OMNI_PRICING.estimatedCostPerSecondUsdByResolution['720p']
/** @deprecated Read the Gemini video manifest directly. */
export const GEMINI_OMNI_IMAGE_INPUT_ESTIMATED_COST_USD = GEMINI_OMNI_PRICING.estimatedImageInputCostUsd

export const VIDEO_INPUT_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'audio/mpeg',
  'audio/wav'
] as const
export type VideoInputContentType = (typeof VIDEO_INPUT_CONTENT_TYPES)[number]
export const VIDEO_IMAGE_INPUT_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

// Input limits enforced by the video API and pre-checked in the browser.
export const VIDEO_MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const VIDEO_MAX_MEDIA_BYTES = 100 * 1024 * 1024
export const VIDEO_IMAGE_MIN_DIMENSION_PX = 640
export const VIDEO_IMAGE_MAX_DIMENSION_PX = 4096

export function isVideoImageDimensionValid(width: number | undefined, height: number | undefined): boolean {
  return [width ?? 0, height ?? 0].every(
    (size) => size >= VIDEO_IMAGE_MIN_DIMENSION_PX && size <= VIDEO_IMAGE_MAX_DIMENSION_PX
  )
}

export type VideoInputRole = 'first' | 'last' | 'reference' | 'video-reference' | 'audio-reference' | 'source'
export type VideoInputKind = 'image' | 'video' | 'audio'
export type VideoGenerationStatus =
  | 'DRAFT'
  | 'QUEUED'
  | 'PREPARING_INPUTS'
  | 'SUBMITTING'
  | 'SUBMITTED'
  | 'GENERATING'
  | 'SAVING'
  | 'READY'
  | 'FAILED'
  | 'CANCELED'

export type VideoGenerationSettings = {
  mode: VideoMode
  model: VideoModel
  prompt: string
  duration: number
  aspectRatio: VideoAspectRatio
  resolution: VideoResolution
  audio: boolean
  negativePrompt?: string
}

export type VideoInputDescriptor = {
  inputId: string
  role: VideoInputRole
  /** Missing on image-only generations created before mixed-media support. */
  kind?: VideoInputKind
  fileName: string
  contentType: VideoInputContentType
  sizeBytes: number
  width?: number
  height?: number
  durationSeconds?: number
  sha256: string
}

export function videoInputKind(input: VideoInputDescriptor): VideoInputKind {
  return input.kind ?? 'image'
}

export type VideoGenerationFailure = {
  code: string
  message: string
  reason?: string
  retryable: boolean
}

export type VideoGenerationQuote = {
  provider: VideoProvider
  /** Runway-specific provider credits. Vertex bills in tokens instead. */
  credits?: number
  estimatedCostUsd: number
  /** Actual token usage and provider charges may differ from this quote. */
  isEstimate: boolean
  pricingVersion: string
}

/** @deprecated Use VideoGenerationQuote. Kept as a stable API contract name. */
export type VideoCreditQuote = VideoGenerationQuote

export type VideoGeneration = {
  createdBy?: GenerationCreator
  generationId: string
  workspaceId: 'shared'
  workspaceStatus: string
  status: VideoGenerationStatus
  progress?: number
  providerState?: 'PENDING' | 'THROTTLED' | 'RUNNING'
  settings: VideoGenerationSettings
  inputs: VideoInputDescriptor[]
  sourceGenerationId?: string
  quote: VideoGenerationQuote
  createdAt: string
  updatedAt: string
  submittedAt?: string
  completedAt?: string
  failure?: VideoGenerationFailure
  /** Display-safe source for the first saved input. Hydrated through Image CDN. */
  posterAsset?: ImageAssetReference
  output?: { sizeBytes?: number; contentType: 'video/mp4' }
}

export type CreateVideoDraftRequest = {
  generationId: string
  settings: VideoGenerationSettings
  inputs: VideoInputDescriptor[]
  sourceGenerationId?: string
  /** Reuse matching saved input IDs from this generation; new IDs are uploaded normally. */
  reuseFromGenerationId?: string
}

export type CreateVideoDraftResponse = {
  generation: VideoGeneration
  uploads: Array<{ inputId: string; uploadUrl: string; expiresAt: string }>
}

export type VideoGenerationHistoryResponse = {
  generations: VideoGeneration[]
  nextCursor?: string
}

export type RunwayCreditsResponse = {
  credits: number
}

export type VideoUsageMetric = {
  generations: number
  credits: number
  estimatedCostUsd: number
}

export type AdminVideoMetrics = {
  totalGenerations: number
  totalCredits: number
  estimatedCostUsd: number
  byModel: Record<VideoModel, VideoUsageMetric>
}

export const DEFAULT_VIDEO_SETTINGS: Record<VideoMode, VideoGenerationSettings> = {
  text: {
    mode: 'text',
    model: 'gen4.5',
    prompt: '',
    duration: 5,
    aspectRatio: '16:9',
    resolution: '720p',
    audio: false
  },
  frames: {
    mode: 'frames',
    model: 'gen4.5',
    prompt: '',
    duration: 5,
    aspectRatio: '16:9',
    resolution: '720p',
    audio: false
  },
  references: {
    mode: 'references',
    model: 'seedance2_5',
    prompt: '',
    duration: 4,
    aspectRatio: '16:9',
    resolution: '720p',
    audio: false
  },
  edit: {
    mode: 'edit',
    model: 'seedance2_5',
    prompt: '',
    duration: 5,
    aspectRatio: '16:9',
    resolution: '720p',
    audio: false
  },
  extend: {
    mode: 'extend',
    model: 'seedance2_5',
    prompt: '',
    duration: 5,
    aspectRatio: '16:9',
    resolution: '720p',
    audio: false
  }
}

/** Statuses a submitted generation can be listed/filtered by in history (every status except DRAFT). */
export const VIDEO_HISTORY_STATUSES = [
  'QUEUED',
  'PREPARING_INPUTS',
  'SUBMITTING',
  'SUBMITTED',
  'GENERATING',
  'SAVING',
  'READY',
  'FAILED',
  'CANCELED'
] as const satisfies readonly VideoGenerationStatus[]
export type VideoHistoryStatus = (typeof VIDEO_HISTORY_STATUSES)[number]

export function isVideoHistoryStatus(value: unknown): value is VideoHistoryStatus {
  return VIDEO_HISTORY_STATUSES.includes(value as VideoHistoryStatus)
}

export const TERMINAL_VIDEO_STATUSES: readonly VideoGenerationStatus[] = ['READY', 'FAILED', 'CANCELED']
export const ACTIVE_VIDEO_STATUSES: readonly VideoGenerationStatus[] = [
  'QUEUED',
  'PREPARING_INPUTS',
  'SUBMITTING',
  'SUBMITTED',
  'GENERATING',
  'SAVING'
]

export function getVideoModelDefinition(model: VideoModel) {
  return VIDEO_MODEL_REGISTRY[model]
}

export function getVideoModelMetadata(model: VideoModel): VideoModelMetadata {
  return VIDEO_MODEL_METADATA[model]
}

/**
 * Whether a `model` request continuing `sourceModel`'s output reuses the
 * provider's stored result, exempting that source from source-duration and
 * total-media-size limits (see `reusesOwnSourceOutput` in the manifest).
 */
export function reusesOwnSourceOutput(model: VideoModel, sourceModel: VideoModel | undefined): boolean {
  return Boolean(VIDEO_MODEL_CAPABILITIES[model].reusesOwnSourceOutput) && model === sourceModel
}

const modelName = (model: VideoModel) => VIDEO_MODEL_METADATA[model].modelName

/** User-facing messages for manifest input limits, shared by the browser pre-check and the API. */
export const VIDEO_LIMIT_MESSAGES = {
  maxVideoSeconds: (model: VideoModel, seconds: number) =>
    `${modelName(model)} video references must be ${seconds} seconds or shorter.`,
  maxSourceVideoSeconds: (model: VideoModel, seconds: number) =>
    `${modelName(model)} source videos must be ${seconds} seconds or shorter.`,
  maxTotalMediaBytes: (model: VideoModel, bytes: number) =>
    `${modelName(model)} media inputs must total ${bytes / (1024 * 1024)} MB or less.`,
  maxInputVideos: (model: VideoModel, count: number) =>
    `${modelName(model)} accepts at most ${count} input videos, including the source.`,
  combinedSeconds: (model: VideoModel, seconds: number) =>
    `${modelName(model)} input video and audio must total ${seconds} seconds or less.`
}

export function getVideoProvider(model: VideoModel): VideoProvider {
  return getVideoModelDefinition(model).provider
}

export function getVideoProviderModelId(model: VideoModel): string {
  return getVideoModelDefinition(model).providerModel
}

export function isVideoModel(value: string): value is VideoModel {
  // Avoid `in`: it also matches inherited keys such as "constructor".
  return (VIDEO_MODELS as readonly string[]).includes(value)
}

export function getVideoAspectRatios(model: VideoModel, mode: VideoMode): readonly VideoAspectRatio[] {
  const capability = VIDEO_MODEL_CAPABILITIES[model]
  return capability.aspectRatiosByMode?.[mode] ?? capability.aspectRatios
}

/** The reference inputs accepted by this model's endpoint for the selected mode. */
export function getVideoReferenceLimits(model: VideoModel, mode: VideoMode): VideoReferenceLimits | undefined {
  const capability = VIDEO_MODEL_CAPABILITIES[model]
  if (mode === 'text') return undefined
  return capability.referenceLimitsByMode?.[mode] ?? (mode === 'frames' ? undefined : capability.referenceLimits)
}

export function estimateVideoQuote(
  settings: VideoGenerationSettings,
  inputs: number | VideoInputDescriptor[] = 0,
  sourceDurationSeconds = 0
): VideoGenerationQuote {
  const definition = getVideoModelDefinition(settings.model)
  const inputCount =
    typeof inputs === 'number' ? inputs : inputs.filter((input) => videoInputKind(input) === 'image').length
  if (definition.provider === 'vertex') {
    const pricing: GeminiVideoPricing = definition.pricing
    const outputRate = pricing.estimatedCostPerSecondUsdByResolution[settings.resolution]
    if (outputRate === undefined) throw new Error(`Unsupported Gemini video pricing resolution: ${settings.resolution}`)
    const estimatedCostUsd = settings.duration * outputRate + inputCount * pricing.estimatedImageInputCostUsd
    return {
      provider: 'vertex',
      estimatedCostUsd: Math.round(estimatedCostUsd * 100_000) / 100_000,
      isEstimate: true,
      pricingVersion: VIDEO_PRICING_VERSION
    }
  }

  const pricing: RunwayCreditPricing = definition.pricing
  let credits: number
  switch (pricing.kind) {
    case 'per-second':
      credits = pricing.creditsPerSecond * settings.duration
      break
    case 'per-second-with-audio':
      credits = (settings.audio ? pricing.creditsPerSecondWithAudio : pricing.creditsPerSecond) * settings.duration
      break
    case 'resolution-based': {
      const inputVideoSeconds =
        typeof inputs === 'number'
          ? 0
          : inputs
              .filter((input) => videoInputKind(input) === 'video')
              .reduce((total, input) => total + (input.durationSeconds ?? 0), 0)
      const referenceRate = settings.resolution === '1080p' ? 34 : settings.resolution === '720p' ? 15 : 10
      credits = Math.max(
        pricing.minimumCredits,
        (pricing.creditsPerSecondByResolution[settings.resolution] ?? 0) * settings.duration +
          referenceRate * (inputVideoSeconds + sourceDurationSeconds)
      )
      break
    }
  }

  return {
    provider: 'runway',
    credits,
    estimatedCostUsd: estimateVideoCostUsd(credits),
    isEstimate: true,
    pricingVersion: VIDEO_PRICING_VERSION
  }
}

/** @deprecated Use estimateVideoQuote. */
export function estimateVideoCredits(
  settings: VideoGenerationSettings,
  inputs: number | VideoInputDescriptor[] = 0,
  sourceDurationSeconds = 0
): VideoGenerationQuote {
  return estimateVideoQuote(settings, inputs, sourceDurationSeconds)
}

export function estimateVideoCostUsd(credits: number): number {
  return credits * RUNWAY_CREDIT_PRICE_USD
}

export function normalizeVideoSettingsForModel(
  current: VideoGenerationSettings,
  model: VideoModel
): VideoGenerationSettings {
  const capability = VIDEO_MODEL_CAPABILITIES[model]
  const mode = capability.modes.includes(current.mode) ? current.mode : (capability.modes[0] ?? 'text')
  const defaults = DEFAULT_VIDEO_SETTINGS[mode]
  const aspectRatios = getVideoAspectRatios(model, mode)
  const durationSupported = capability.durations.values
    ? capability.durations.values.includes(current.duration)
    : Number.isInteger(current.duration) &&
      current.duration >= capability.durations.min &&
      current.duration <= capability.durations.max
  const defaultDuration = capability.durations.values?.[0] ?? capability.durations.min
  return {
    ...defaults,
    mode,
    model,
    prompt: current.prompt,
    duration: durationSupported ? current.duration : defaultDuration,
    aspectRatio: aspectRatios.includes(current.aspectRatio)
      ? current.aspectRatio
      : (aspectRatios[0] ?? defaults.aspectRatio),
    resolution: capability.resolutions.includes(current.resolution)
      ? current.resolution
      : capability.resolutions.includes(defaults.resolution)
        ? defaults.resolution
        : (capability.resolutions[0] ?? defaults.resolution),
    audio: capability.audioMode === 'included' ? true : capability.supportsAudio ? current.audio : false,
    negativePrompt: capability.supportsNegativePrompt ? current.negativePrompt : undefined
  }
}

export function validateVideoSettings(settings: VideoGenerationSettings): string[] {
  const capability = VIDEO_MODEL_CAPABILITIES[settings.model]
  const aspectRatios = getVideoAspectRatios(settings.model, settings.mode)
  const errors: string[] = []
  if (!capability.modes.includes(settings.mode)) errors.push('The selected model does not support this mode.')
  if (!settings.prompt.trim()) errors.push('A prompt is required.')
  if (settings.prompt.length > capability.promptMaxLength) errors.push('The prompt is too long for this model.')
  if (!aspectRatios.includes(settings.aspectRatio)) errors.push('Unsupported aspect ratio.')
  if (!capability.resolutions.includes(settings.resolution)) errors.push('Unsupported resolution.')
  const validDuration = capability.durations.values
    ? capability.durations.values.includes(settings.duration)
    : Number.isInteger(settings.duration) &&
      settings.duration >= capability.durations.min &&
      settings.duration <= capability.durations.max
  if (!validDuration) errors.push('Unsupported duration.')
  if (settings.audio && !capability.supportsAudio) errors.push('Audio is not supported by this model.')
  if (capability.audioMode === 'included' && !settings.audio) errors.push('Native audio is required by this model.')
  if (settings.negativePrompt && !capability.supportsNegativePrompt) errors.push('Negative prompt is not supported.')
  return errors
}
import type { ImageAssetReference } from '../image-assets'
